import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react';
import { isoDateTime, type ISODateTime } from '@bigint-project/core';

import type { ProjectTemplate } from '../../persistence/project-template';

export interface ProjectTemplateCreateInput {
  readonly projectName: string;
  readonly anchorDate?: ISODateTime;
}

interface ProjectTemplateCreateDialogProps {
  readonly isOpen: boolean;
  readonly template: ProjectTemplate;
  readonly onCancel: () => void;
  readonly onCreate: (input: ProjectTemplateCreateInput) => void;
}

/** 範本的排程錨點；`scheduleFrom` 決定平移要對齊開始日還是完成日。 */
function anchorOf(template: ProjectTemplate): ISODateTime {
  return template.project.scheduleFrom === 'ProjectStart' ? template.project.startDate : template.project.finishDate;
}

/** 只換掉日期部分，保留範本原本的上班時刻，避免平移順便改動一天內的起訖時間。 */
function replaceDate(value: ISODateTime, date: string): ISODateTime {
  return isoDateTime(`${date}${(value as string).slice(10)}`);
}

export function ProjectTemplateCreateDialog({ isOpen, template, onCancel, onCreate }: ProjectTemplateCreateDialogProps): ReactElement | null {
  const anchor = anchorOf(template);
  const canRebase = template.captureMode === 'Planning';
  const anchorLabel = template.project.scheduleFrom === 'ProjectStart' ? '新專案開始日' : '新專案完成日';
  const [projectName, setProjectName] = useState(template.metadata.name);
  const [anchorInput, setAnchorInput] = useState((anchor as string).slice(0, 10));
  const [errors, setErrors] = useState<Partial<Record<'projectName' | 'anchorDate', string>>>({});
  const titleId = useId();
  const dialogRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setProjectName(template.metadata.name);
      setAnchorInput((anchorOf(template) as string).slice(0, 10));
      setErrors({});
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, template]);

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nameInputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors: Partial<Record<'projectName' | 'anchorDate', string>> = {};
    if (!projectName.trim()) nextErrors.projectName = '請輸入專案名稱。';
    if (canRebase && !anchorInput) nextErrors.anchorDate = `請選擇${anchorLabel}。`;
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onCreate({
      projectName: projectName.trim(),
      anchorDate: canRebase ? replaceDate(anchor, anchorInput) : undefined,
    });
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const items = [...focusable];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <form
        aria-labelledby={titleId}
        aria-modal="true"
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900"
        ref={dialogRef}
        onKeyDown={trapFocus}
        onSubmit={handleSubmit}
        role="dialog"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-base font-semibold">從範本建立專案</h2>
          <button type="button" onClick={onCancel} aria-label="關閉從範本建立專案" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">關閉</button>
        </div>

        <div className="grid gap-3 text-sm">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            範本「{template.metadata.name}」·{canRebase ? '規劃範本' : '快照範本'}·任務 {template.project.tasks.length}、資源 {template.project.resources.length}
            {template.metadata.description === undefined ? null : <span className="mt-1 block">{template.metadata.description}</span>}
          </p>
          <label className="grid gap-1">
            專案名稱
            <input ref={nameInputRef} aria-invalid={Boolean(errors.projectName)} aria-describedby={errors.projectName ? 'template-project-name-error' : undefined} value={projectName} onChange={(event) => setProjectName(event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
            {errors.projectName ? <span id="template-project-name-error" className="text-xs text-red-600">{errors.projectName}</span> : null}
          </label>
          {canRebase ? (
            <label className="grid gap-1">
              {anchorLabel}
              <input type="date" aria-invalid={Boolean(errors.anchorDate)} aria-describedby={errors.anchorDate ? 'template-anchor-date-error' : undefined} value={anchorInput} onChange={(event) => setAnchorInput(event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
              {errors.anchorDate
                ? <span id="template-anchor-date-error" className="text-xs text-red-600">{errors.anchorDate}</span>
                : <span className="text-xs text-slate-500 dark:text-slate-400">所有日期會依這個錨點整批平移，再依工作日曆重新排程。</span>}
            </label>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">快照範本會原樣保留 {(anchor as string).slice(0, 10)} 起算的既有日期，不可平移。</p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800">取消</button>
          <button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">建立專案</button>
        </div>
      </form>
    </div>
  );
}
